import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Music, MapPin, Calendar, Users, Ticket, Heart, Sparkles, Star } from 'lucide-react';
import axios from 'axios';
import SEO from '../components/SEO';
import { createOrganizationSchema, createWebSiteSchema, createEventSchema, DEFAULT_FAQ_SCHEMA } from '../utils/seoSchemas';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BASE_URL = 'https://www.invasionlatina.be';

// Standalone Spanish landing page — targets ES queries ("fiesta latina bruselas",
// "reggaeton bruselas", "donde bailar reggaeton bruselas", etc.).
// Independent route /es with full Spanish content, Spanish meta, hreflang to FR home.
const EspanolPage = () => {
  const [featuredEvent, setFeaturedEvent] = useState(null);
  const [openairDate, setOpenairDate] = useState('2026-07-11T14:00:00');
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0 });

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/events/featured?lang=es`)
      .then((r) => {
        if (r.data?.event) setFeaturedEvent(r.data.event);
        else if (r.data?.length > 0) setFeaturedEvent(r.data[0]);
      })
      .catch(() => {});
    axios.get(`${BACKEND_URL}/api/countdown`)
      .then((r) => r.data?.target_date && setOpenairDate(r.data.target_date))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const tick = () => {
      const diff = new Date(openairDate).getTime() - Date.now();
      if (diff <= 0) return setTimeLeft({ days: 0, hours: 0, minutes: 0 });
      setTimeLeft({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff / 3600000) % 24),
        minutes: Math.floor((diff / 60000) % 60),
      });
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [openairDate]);

  // Combined Spanish-localized schema graph
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      { ...createWebSiteSchema(), inLanguage: 'es-ES' },
      { ...createOrganizationSchema(), inLanguage: 'es-ES' },
      DEFAULT_FAQ_SCHEMA,
      ...(featuredEvent ? [{ ...createEventSchema(featuredEvent), inLanguage: 'es-ES' }] : []),
    ],
  };

  return (
    <div className="min-h-screen bg-[#060606] text-[#F5F0E8]" data-testid="espanol-page">
      <SEO
        title="Reggaeton Bruselas | Invasion Latina — Fiesta Latina más Grande de Bélgica"
        description="Invasion Latina es la fiesta reggaeton y latina más grande de Bruselas desde 2009. Eventos mensuales en el Mirano Continental + Open Air anual 11/07/2026 en Place du Congrès. Reggaeton, latin trap, dembow, bachata. Tickets online."
        keywords="reggaeton bruselas, fiesta latina bruselas, fiesta reggaeton bruselas, discoteca latina bruselas, donde bailar reggaeton bruselas, salir bruselas reggaeton, eventos latinos belgica, mejor fiesta latina bruselas, mirano continental, open air bruselas 2026, invasion latina, bachata bruselas, dembow bruselas, latin trap bruselas"
        canonical="/es"
        htmlLang="es-ES"
        ogLocale="es_ES"
        ogType="website"
        structuredData={schema}
        hreflang={[
          { lang: 'es', url: '/es' },
          { lang: 'fr', url: '/' },
          { lang: 'x-default', url: '/' },
        ]}
      />

      {/* HERO */}
      <section className="relative min-h-[80vh] flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0B0C] via-[#060606] to-[#060606]" />
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#C9A84C]/20 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#E2C56A]/10 rounded-full blur-3xl" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 max-w-4xl mx-auto text-center"
        >
          <div className="inline-block mb-6 px-4 py-2 border border-[#C9A84C]/30 rounded-full">
            <span className="text-xs uppercase tracking-[0.3em] text-[#C9A84C] font-display">
              🇪🇸 Versión en Español
            </span>
          </div>

          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight text-[#F5F0E8] mb-6 leading-tight">
            La fiesta <span className="text-[#C9A84C]">reggaeton</span> más grande de Bruselas
          </h1>

          <p className="font-serif italic text-xl sm:text-2xl text-[#B9B2A8] mb-4 max-w-3xl mx-auto">
            Invasion Latina — desde 2009
          </p>

          <p className="text-base sm:text-lg text-[#B9B2A8] mb-10 max-w-2xl mx-auto leading-relaxed">
            16 años organizando los mejores eventos de música urbana latina en Bélgica.
            Reggaeton, latin trap, dembow, bachata. Más de 240.000 tickets vendidos.
            Una sola familia.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="/#tickets"
              className="px-8 py-4 bg-[#C9A84C] text-[#060606] font-display uppercase tracking-wider rounded-full hover:bg-[#E2C56A] transition-all shadow-lg shadow-[#C9A84C]/20"
            >
              Ver próximas entradas →
            </a>
            <Link
              to="/booking"
              className="px-8 py-4 border border-[#C9A84C]/40 text-[#C9A84C] font-display uppercase tracking-wider rounded-full hover:bg-[#C9A84C]/10 transition-all"
            >
              Reservar mesa VIP
            </Link>
          </div>
        </motion.div>
      </section>

      {/* COUNTDOWN OPEN AIR */}
      <section className="py-16 sm:py-24 bg-[#0B0B0C] border-y border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-xs uppercase tracking-[0.3em] text-[#C9A84C] mb-3">El evento del año</p>
            <h2 className="font-display text-4xl sm:text-5xl text-[#F5F0E8] mb-2">
              Open Air 2026
            </h2>
            <p className="font-serif italic text-lg text-[#B9B2A8] mb-8">
              Sábado 11 de julio · Place du Congrès, Bruselas
            </p>

            <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto mb-8">
              {[
                { value: timeLeft.days, label: 'Días' },
                { value: timeLeft.hours, label: 'Horas' },
                { value: timeLeft.minutes, label: 'Minutos' },
              ].map(({ value, label }) => (
                <div key={label} className="bg-white/5 border border-white/10 rounded-2xl p-4">
                  <div className="font-display text-4xl text-[#C9A84C]">{value}</div>
                  <div className="text-xs uppercase tracking-wider text-[#B9B2A8] mt-1">{label}</div>
                </div>
              ))}
            </div>

            <p className="text-[#B9B2A8] max-w-2xl mx-auto">
              7.000 personas, 8 horas de reggaeton sin parar, 7 DJs residentes, el mejor sound system de Bélgica.
              No es un festival. Es nuestro verano.
            </p>
          </motion.div>
        </div>
      </section>

      {/* LA MÚSICA */}
      <section className="py-16 sm:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-[#F5F0E8] mb-3">
              La música
            </h2>
            <p className="font-serif italic text-lg text-[#B9B2A8]">
              Toda la cultura urbana latina en un mismo lugar
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {['Reggaeton', 'Latin Trap', 'Dembow', 'Bachata', 'Afrobeats', 'Dancehall', 'Mambo', 'Salsa'].map((genre) => (
              <div
                key={genre}
                className="bg-white/5 border border-white/10 rounded-xl p-4 text-center hover:border-[#C9A84C]/30 hover:bg-white/10 transition-all"
              >
                <Music className="w-5 h-5 text-[#C9A84C] mx-auto mb-2" />
                <span className="font-display text-sm uppercase tracking-wider text-[#F5F0E8]">{genre}</span>
              </div>
            ))}
          </div>

          <p className="text-center text-[#B9B2A8] mt-12 max-w-3xl mx-auto leading-relaxed">
            En Invasion Latina pinchamos toda la <strong className="text-[#F5F0E8]">música urbana latina</strong> que mueve la pista: reggaeton clásico y nuevo, latin trap, dembow dominicano, bachata para enamorarse y afrobeats latino. Nuestros DJs residentes mezclan sin parar durante toda la noche. Es la referencia <strong className="text-[#F5F0E8]">reggaeton en Bruselas</strong> desde hace 16 años.
          </p>
        </div>
      </section>

      {/* LOS LUGARES */}
      <section className="py-16 sm:py-24 bg-[#0B0B0C]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-[#F5F0E8] mb-3">
              Dónde están las fiestas
            </h2>
            <p className="font-serif italic text-lg text-[#B9B2A8]">
              Dos venues legendarios en Bruselas
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white/5 border border-white/10 rounded-2xl p-8"
            >
              <div className="flex items-center gap-3 mb-4">
                <MapPin className="w-6 h-6 text-[#C9A84C]" />
                <h3 className="font-display text-2xl text-[#F5F0E8]">Mirano Continental</h3>
              </div>
              <p className="text-sm text-[#B9B2A8] mb-3">
                Chaussée de Louvain 38<br />1210 Saint-Josse-ten-Noode, Bruselas
              </p>
              <p className="text-[#B9B2A8] leading-relaxed">
                Nuestra residencia mensual. 1.800 personas, ambiente íntimo, escenografía latina, ambiente familia. Es <em>el</em> spot reggaeton de Bruselas. Eventos casi todos los meses — consulta la agenda.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white/5 border border-[#C9A84C]/30 rounded-2xl p-8"
            >
              <div className="flex items-center gap-3 mb-4">
                <Sparkles className="w-6 h-6 text-[#C9A84C]" />
                <h3 className="font-display text-2xl text-[#F5F0E8]">Place du Congrès</h3>
              </div>
              <p className="text-sm text-[#B9B2A8] mb-3">
                Place du Congrès<br />1000 Bruselas
              </p>
              <p className="text-[#B9B2A8] leading-relaxed">
                Cada año: el Open Air. 7.000 personas, plein air, 8 horas non-stop, el evento más esperado del verano latino en Bélgica. Próxima edición el 11 de julio de 2026.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* TICKETS */}
      <section className="py-16 sm:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-[#F5F0E8] mb-3">
              Cómo comprar tickets
            </h2>
            <p className="font-serif italic text-lg text-[#B9B2A8]">
              Más temprano compres, menos cuesta
            </p>
          </motion.div>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8 mb-8">
            <p className="text-[#B9B2A8] leading-relaxed mb-6">
              Vendemos los tickets en <strong className="text-[#F5F0E8]">vagues</strong> (olas de precio). Cuanto antes compres, menos pagas. Cuando una ola se agota, pasamos a la siguiente con un precio un poco más alto.
            </p>
            <ul className="space-y-3 text-[#B9B2A8]">
              <li className="flex items-start gap-3">
                <Ticket className="w-5 h-5 text-[#C9A84C] mt-0.5 shrink-0" />
                <span><strong className="text-[#F5F0E8]">Early Birds</strong> — los primeros, el mejor precio (suele agotarse rápido)</span>
              </li>
              <li className="flex items-start gap-3">
                <Ticket className="w-5 h-5 text-[#C9A84C] mt-0.5 shrink-0" />
                <span><strong className="text-[#F5F0E8]">Wave 1 / 2 / 3</strong> — las olas siguientes, según disponibilidad</span>
              </li>
              <li className="flex items-start gap-3">
                <Star className="w-5 h-5 text-[#C9A84C] mt-0.5 shrink-0" />
                <span><strong className="text-[#F5F0E8]">VIP</strong> — acceso a la zona VIP, servicio premium, ambiente exclusivo</span>
              </li>
            </ul>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/agenda"
              className="px-8 py-4 bg-[#C9A84C] text-[#060606] font-display uppercase tracking-wider rounded-full hover:bg-[#E2C56A] transition-all text-center"
            >
              Ver agenda completa
            </Link>
            <Link
              to="/booking"
              className="px-8 py-4 border border-[#C9A84C]/40 text-[#C9A84C] font-display uppercase tracking-wider rounded-full hover:bg-[#C9A84C]/10 transition-all text-center"
            >
              Reservar VIP
            </Link>
          </div>
        </div>
      </section>

      {/* 16 ANS HISTORIA */}
      <section className="py-16 sm:py-24 bg-[#0B0B0C]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-[#F5F0E8] mb-3">
              16 años haciendo bailar Bruselas
            </h2>
            <p className="font-serif italic text-lg text-[#B9B2A8]">
              Desde mayo de 2009
            </p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mb-10">
            {[
              { value: '16', label: 'Años' },
              { value: '1 200+', label: 'Eventos' },
              { value: '240K+', label: 'Tickets vendidos' },
              { value: '7 000', label: 'Open Air 2026' },
            ].map(({ value, label }) => (
              <div key={label} className="text-center">
                <div className="font-display text-4xl text-[#C9A84C] mb-1">{value}</div>
                <div className="text-xs uppercase tracking-wider text-[#B9B2A8]">{label}</div>
              </div>
            ))}
          </div>

          <p className="text-[#B9B2A8] leading-relaxed max-w-3xl mx-auto text-center">
            Invasion Latina nació en 2009 de una pasión simple: traer la cultura urbana latina a Bélgica. Empezamos con eventos pequeños, hoy somos la fiesta reggaeton más grande del país. Tres generaciones de la comunidad latina bruselense han bailado con nosotros. Esto no es una discoteca, es una familia.
          </p>
        </div>
      </section>

      {/* VALEURS / SAFE SPACE */}
      <section className="py-16 sm:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Heart className="w-10 h-10 text-[#C9A84C] mx-auto mb-4" />
            <h2 className="font-display text-3xl sm:text-4xl text-[#F5F0E8] mb-4">
              Safe space, ambiente familia
            </h2>
            <p className="text-[#B9B2A8] max-w-2xl mx-auto leading-relaxed">
              Cero tolerancia al acoso. LGBTQ+ friendly. Aquí todo el mundo viene a bailar, a pasarlo bien y a respetarse. Si tienes un problema durante una de nuestras fiestas, busca a un miembro del staff con chaleco — estamos para ti.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-20 sm:py-32 bg-gradient-to-b from-[#0B0B0C] to-[#060606]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Users className="w-12 h-12 text-[#C9A84C] mx-auto mb-6" />
            <h2 className="font-display text-4xl sm:text-5xl text-[#F5F0E8] mb-4">
              Únete a la familia
            </h2>
            <p className="text-[#B9B2A8] mb-8 max-w-xl mx-auto leading-relaxed">
              Síguenos en redes sociales, suscríbete a la newsletter, baja la app móvil. No te pierdas ningún evento.
            </p>

            <div className="flex flex-wrap gap-3 justify-center mb-8">
              <a href="https://instagram.com/invasionlatina" target="_blank" rel="noopener noreferrer" className="px-5 py-2 bg-white/5 border border-white/10 rounded-full text-sm hover:bg-white/10 transition-all">Instagram</a>
              <a href="https://tiktok.com/@invasionlatina" target="_blank" rel="noopener noreferrer" className="px-5 py-2 bg-white/5 border border-white/10 rounded-full text-sm hover:bg-white/10 transition-all">TikTok</a>
              <a href="https://facebook.com/invasionlatina" target="_blank" rel="noopener noreferrer" className="px-5 py-2 bg-white/5 border border-white/10 rounded-full text-sm hover:bg-white/10 transition-all">Facebook</a>
              <a href="https://youtube.com/@invasionlatina" target="_blank" rel="noopener noreferrer" className="px-5 py-2 bg-white/5 border border-white/10 rounded-full text-sm hover:bg-white/10 transition-all">YouTube</a>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/agenda"
                className="px-8 py-4 bg-[#C9A84C] text-[#060606] font-display uppercase tracking-wider rounded-full hover:bg-[#E2C56A] transition-all"
              >
                Próximos eventos
              </Link>
              <Link
                to="/"
                className="px-8 py-4 border border-white/20 text-[#F5F0E8] font-display uppercase tracking-wider rounded-full hover:bg-white/5 transition-all"
              >
                Version française →
              </Link>
            </div>

            <p className="mt-12 text-xs text-[#B9B2A8]/60">
              <Calendar className="w-3 h-3 inline mr-1" />
              Invasion Latina — Reggaeton Bruselas · Brussels · Bruxelles desde 2009
            </p>
          </motion.div>
        </div>
      </section>

      {/* Padding pour mobile tickets bar */}
      <div className="h-20 lg:h-0" />
    </div>
  );
};

export default EspanolPage;
